import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { getProduct } from '#/products/domain';
import { useUMPlatformCreationDisabled } from '#/platform-state/contexts/providers';
import { STRATEGIES } from '#/shared/strategies';
import { INITIAL_STATE, State } from './store';

interface RfqBuilderModalMethods {
  readonly open: (
    prefill?: Partial<State>,
    onError?: (message: string) => void,
  ) => void;
  readonly close: () => void;
}

interface RfqBuilderModalState {
  readonly isOpen: boolean;
  readonly prefill: Partial<State>;
}

export const StateCtx = createContext<RfqBuilderModalState>({
  isOpen: false,
  prefill: {},
});
StateCtx.displayName = 'RfqBuilderModalState';

const throwMissingProvider = () => {
  throw new Error('Missing RfqBuilderModalProvider upwards in this tree');
};

export const MethodsCtx = createContext<RfqBuilderModalMethods>({
  close: throwMissingProvider,
  open: throwMissingProvider,
});
MethodsCtx.displayName = 'RfqBuilderModalMethods';

interface ProviderProps {
  readonly children: ReactNode;
}

export const RfqBuilderModalProvider = ({ children }: ProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prefill, setPrefill] = useState<Partial<State>>({});
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const state: RfqBuilderModalState = {
    isOpen: isPlatformCreationDisabled ? false : isOpen,
    prefill,
  };

  const methods = useMemo(
    (): RfqBuilderModalMethods => ({
      open: (prefillState, onError) => {
        if (
          prefillState?.productCode !== null &&
          prefillState?.productCode !== undefined
        ) {
          const product = getProduct(prefillState.productCode);
          if (product.isDisabled === true) {
            onError?.(`Product ${product.shortName} was disabled.`);
            setPrefill({
              ...INITIAL_STATE,
              auction: prefillState.auction,
              counterparties: prefillState.counterparties,
            });
          }
          if (isUndefinedStrategy(prefillState.strategyCode)) {
            setPrefill({
              ...prefillState,
              strategyCode: null,
            });
          } else {
            setPrefill(prefillState);
          }
        }
        setIsOpen(true);
      },
      close: () => {
        setPrefill({});
        setIsOpen(false);
      },
    }),
    [],
  );

  return (
    <StateCtx.Provider value={state}>
      <MethodsCtx.Provider value={methods}>{children}</MethodsCtx.Provider>
    </StateCtx.Provider>
  );
};

export const useRfqBuilderModalState = () => {
  const ctx = useContext(StateCtx);
  return ctx;
};

export const useRfqBuilderModalMethods = () => {
  const ctx = useContext(MethodsCtx);
  return ctx;
};

function isUndefinedStrategy(strategyCode: State['strategyCode']): boolean {
  const isDefinedStrategyCode =
    strategyCode != null && strategyCode in STRATEGIES;
  return !isDefinedStrategyCode;
}
